import React, { useState, useEffect, ChangeEvent, useMemo } from 'react';

import Title from '@vromo/ui/components/Title';
import { ModalHeader, ModalContent, ModalActions } from '@vromo/ui/components/Modal';
import Button from '@vromo/ui/components/Button';
import Select from '@vromo/ui/components/Select';
import Input from '@vromo/ui/components/Input';

import { action, runInAction, when } from 'mobx';
import capitalize from 'lodash/capitalize';
import { useStore } from '@vromo/mobx-react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { update } from 'serializr';
import { useToasts } from '@vromo/ui/components/Toasts';
import { EntityList } from '@vromo/models';
import { Role } from '@vromo/models';
import { RootStore } from '../../stores/RootStore';
import { isValidEmail } from '@vromo/models/helpers';
import { UserAccessPosition } from '@vromo/api';
import { qaLabelTeams } from './../../../../../common-qa-labels/teams';

type NewDashboardUserFormProps = {
    roles: EntityList<Role>;
    close: () => void;
};


export default observer(function NewDashboardUserForm({ roles, close }: NewDashboardUserFormProps) {
    const {
        groupStore,
        authStore: { role, graph, isFreemium }
    } = useStore<RootStore>('rootStore');

    const showToast = useToasts();

    const store = useLocalObservable(() => ({
        get positions() {
            return {
                admin: isFreemium ? UserAccessPosition.freemiumAdmin : UserAccessPosition.admin,
                user: isFreemium ? UserAccessPosition.freemiumUser : UserAccessPosition.user,
                client: UserAccessPosition.client
            } as { [key: string]: number };
        }
    }));

    const [group, setGroup] = useState(0);
    const [email, setEmail] = useState('');
    const [position, setPosition] = useState(store.positions.user);

    useEffect(() => {
        when(
            () => groupStore.loaded,
            action(() => {
                setGroup(groupStore.groups.first()!.id!);
            })
        );
    }, []);

    const emailsInGroup = useMemo(
        () =>
            roles
                .filter((item) => item.account?.id === group)
                .map((item) => item.user?.contact?.email?.toLowerCase().trim() || ''),
        [group, roles]
    );

    const isValidUser = () => {
        const allFieldsFilled = email.length !== 0;

        const validEmail = isValidEmail(email);

        const emailIsNotUsedInGroup = !emailsInGroup.includes(email.toLowerCase().trim());

        const showError = (validated: boolean, message: string) => {
            if (validated) {
                return true;
            } else {
                showToast(message);
                return false;
            }
        };

        return (
            showError(allFieldsFilled, 'Please fill out all fields before adding a new user.') &&
            showError(validEmail, `The email you've entered is invalid!`) &&
            showError(emailIsNotUsedInGroup, 'This user already exists within this group!')
        );
    };

    async function inviteUser() {
        if (!isValidUser()) return;

        const postData = new EntityList(Role)
        postData.push({
            position: { id: position! },
            user: { contact: { email: email } },
            account: { id: group }
        } as Role)

        const { items: accountRoles } = await graph.root
            .graph()
            .roles(role.id)
            .account()
            .roles()
            .post(postData)

        runInAction(() => {
            update(roles, { items: accountRoles, type: 'create' });
            close();
            showToast(`Successfully added `);
        })
    }

    return (
        <div>
            <ModalHeader>
                <Title data-testid={qaLabelTeams.newDashboardUserModal.title}>Invite a New Dashboard User</Title>
            </ModalHeader>
            <ModalContent>
                <Input
                    label="Email address"
                    placeholder="e.g. hello@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid={qaLabelTeams.newDashboardUserModal.emailInput}
                />
                <label style={{ paddingTop: 8 }}>Role</label>
                <Select
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                        setPosition(parseInt(e.target.value))
                    }
                    data-testid={qaLabelTeams.newDashboardUserModal.roleDropdown}
                >
                    {Object.keys(store.positions).map((it) => (
                        <option
                            key={store.positions[it]}
                            value={store.positions[it]}
                            selected={position === store.positions[it]}
                        >
                            {capitalize(it)}
                        </option>
                    ))}
                </Select>
                <label style={{ paddingTop: 8 }}>Group</label>
                <Select
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                        setGroup(parseInt(e.target.value))
                    }
                    data-testid={qaLabelTeams.newDashboardUserModal.groupDropdown}
                >
                    {groupStore.groups.map((it) => (
                        <option key={it.id} value={it.id!}>
                            {it.name}
                        </option>
                    ))}
                </Select>
            </ModalContent>

            <ModalActions>
                <Button onClick={close} data-testid={qaLabelTeams.newDashboardUserModal.cancelButton}>
                    Cancel
                </Button>
                <Button onClick={inviteUser} data-testid={qaLabelTeams.newDashboardUserModal.saveButton} primary>
                    Save
                </Button>
            </ModalActions>
        </div>
    );
});
